import { Injectable } from '@angular/core';
import { createWorker, PSM } from 'tesseract.js';

export interface MeterInfo {
  location?: string;
  currentReading?: string;
  meterBrand?: string;
  serialNumber?: string;
  type?: string;
  voltage?: string;
  currentRating?: string;
  configuration?: string;
  frequency?: string;
  accuracyClass?: string;
  standard?: string;
  meterConstant?: string;
  manufacturer?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MeterOcrService {
  async enhanceImage(base64: string, threshold = 128, invert = true): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(base64);
          return;
        }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          const contrast = avg > threshold ? 255 : 0;
          const finalValue = invert ? 255 - contrast : contrast;
          data[i] = data[i + 1] = data[i + 2] = finalValue;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL());
      };
      img.src = base64;
    });
  }

  async runTesseract(image: string): Promise<string> {
    const worker = await createWorker({ logger: (message) => console.log(message) });

    await worker.load();
    await worker.loadLanguage('eng');
    await worker.initialize('eng');
    await worker.setParameters({
      tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz/-.:%() ',
      tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
    });

    const {
      data: { text },
    } = await worker.recognize(image);

    await worker.terminate();
    return text.trim();
  }

  parseMeterData(rawText: string): MeterInfo {
    const data: MeterInfo = {};
    const text = rawText.replace(/\s+/g, ' ').toUpperCase();

    const readingMatch = text.match(/(\d{2,6}[.,]?\d{0,3})\s*KWH/);
    if (readingMatch) data.currentReading = readingMatch[1].replace(',', '');

    const serialMatch = text.match(/(S\/N|SERIAL)\s*[:\- ]?\s*([A-Z0-9]+)/);
    if (serialMatch) data.serialNumber = serialMatch[2];

    const brandMatch = text.match(/\b(METRIX|LANDIS|ACTARIS|HEXING|SCHNEIDER|SIEMENS)\b/);
    if (brandMatch) data.meterBrand = brandMatch[1];

    const typeMatch = text.match(/\bHX\d{2,3}[A-Z\-0-9]*\b/);
    if (typeMatch) data.type = typeMatch[0];

    const voltageMatch = text.match(/(\d{1,3}X\d{3}\/\d{3}V)/);
    if (voltageMatch) data.voltage = voltageMatch[1];

    const currentMatch = text.match(/(\d{1,3}\-\d{1,3}A)/);
    if (currentMatch) data.currentRating = currentMatch[1];

    const configMatch = text.match(/3\s*PHASE\s*4\s*WIRE/);
    if (configMatch) data.configuration = configMatch[0];

    const freqMatch = text.match(/(50\s*HZ|60\s*HZ)/);
    if (freqMatch) data.frequency = freqMatch[1];

    const classMatch = text.match(/CLASS\s*[0-9.]+/);
    if (classMatch) data.accuracyClass = classMatch[0];

    const stdMatch = text.match(/IEC\s*\d+/);
    if (stdMatch) data.standard = stdMatch[0];

    const constantMatch = text.match(/(\d+\s*REV\S*\/KWH)/);
    if (constantMatch) data.meterConstant = constantMatch[1];

    const manuMatch = text.match(/\b(AC\/DC DYNAMICS|HEXING|LANDIS|ACTARIS|SCHNEIDER|SIEMENS)\b/);
    if (manuMatch) data.manufacturer = manuMatch[1];

    return data;
  }
}
